import React, { useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import styles from "./styles.module.scss";
import WheelDateTimePicker, { type DateTimeValue } from "../wheel-datetime-picker";

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  error?: boolean;
  onChange?: (value: string) => void;
  value?: string;
}

// value(react-hook-form等から復元された "YYYY-MM-DD" 文字列)をピッカーの
// 初期選択値に変換する。これが無いと、既に値が保存されている状態で
// ピッカーを開いた際、保存済みの日付ではなく常に今日の日付から始まってしまう。
export function parseValueToPickerValue(value?: string): DateTimeValue | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return {
    year: String(date.getFullYear()),
    month: String(date.getMonth() + 1).padStart(2, "0"),
    day: String(date.getDate()).padStart(2, "0"),
    hour: "00",
    minute: "00",
  };
}

const DateInput = React.forwardRef<HTMLInputElement, Props>(
  ({ className, error, onChange, value, placeholder = "", ...props }, ref) => {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [pickerValue, setPickerValue] = useState<DateTimeValue | undefined>();

    const handleInputClick = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsPickerOpen(true);
    };

    const handlePickerChange = (newValue: DateTimeValue) => {
      setPickerValue(newValue);
      const dateString = `${newValue.year}-${newValue.month}-${newValue.day}`;
      onChange?.(dateString);
    };

    const formatDisplayValue = (val: DateTimeValue) => {
      return `${val.year}年${val.month}月${val.day}日`;
    };

    const displayValue = pickerValue 
      ? formatDisplayValue(pickerValue)
      : value 
        ? (() => {
            try {
              const date = new Date(value);
              return `${date.getFullYear()}年${String(date.getMonth() + 1).padStart(2, '0')}月${String(date.getDate()).padStart(2, '0')}日`;
            } catch {
              return value;
            }
          })()
        : '';
    
    return (
      <>
        <input
          ref={ref}
          type="text"
          className={clsx(styles.input, error && styles.error, className)}
          value={displayValue}
          placeholder={placeholder}
          onClick={handleInputClick}
          readOnly
          {...props}
        />
        {isPickerOpen && createPortal(
          <WheelDateTimePicker
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            onChange={handlePickerChange}
            value={pickerValue ?? parseValueToPickerValue(value)}
            mode="date"
          />,
          document.querySelector('[data-bottom-sheet-content]') || document.body
        )}
      </>
    );
  }
);

DateInput.displayName = "DateInput";

export { DateInput };
